/**
 * VersionUpdateModal.js
 *
 * Displays version update notifications to users.
 * Shows once per new version, with acknowledge/dismiss functionality.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge, Spinner } from 'react-bootstrap';
import { useI18n } from '../../i18n';
import { versionAPI } from '../../utils/httpClient';
import styled from 'styled-components';

const VersionBadge = styled(Badge)`
    font-size: 1rem;
    padding: 0.5rem 1rem;
    margin-right: 0.5rem;
`;

const ContentContainer = styled.div`
    max-height: 50vh;
    overflow-y: auto;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.5rem;
    white-space: pre-wrap;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 0.95rem;
    line-height: 1.6;

    h1, h2, h3, h4 {
        margin-top: 1rem;
        margin-bottom: 0.5rem;
        font-weight: 600;
    }

    h2 {
        font-size: 1.25rem;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 0.25rem;
    }

    h3 {
        font-size: 1.1rem;
    }

    ul, ol {
        margin-left: 1.5rem;
        margin-bottom: 0.5rem;
    }

    li {
        margin-bottom: 0.25rem;
    }

    code {
        background-color: #e9ecef;
        padding: 0.15rem 0.4rem;
        border-radius: 0.2rem;
        font-size: 0.9em;
    }
`;

const HeaderIcon = styled.span`
    font-size: 1.5rem;
    margin-right: 0.5rem;
`;

const VersionUpdateModal = ({ show, onHide, versionData, onAcknowledge }) => {
    const { t, lang } = useI18n();
    const [acknowledging, setAcknowledging] = useState(false);

    // Format date based on language
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Handle acknowledge button click
    const handleAcknowledge = async () => {
        if (!versionData?.version) return;

        setAcknowledging(true);
        try {
            await versionAPI.acknowledge(versionData.version);
            if (onAcknowledge) {
                onAcknowledge(versionData.version);
            }
            onHide();
        } catch (err) {
            console.error('Failed to acknowledge version:', err);
            // Still close the modal even if acknowledge fails
            onHide();
        } finally {
            setAcknowledging(false);
        }
    };

    // Handle dismiss (remind later) - closes without acknowledging
    const handleDismiss = () => {
        onHide();
    };

    // Simple markdown-like rendering
    const renderContent = (content) => {
        if (!content) return null;

        // Convert markdown-like syntax to HTML
        let html = content
            // Headers
            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
            .replace(/^# (.+)$/gm, '<h1>$1</h1>')
            // Bold
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            // Inline code
            .replace(/`([^`]+)`/g, '<code>$1</code>')
            // List items (simple)
            .replace(/^- (.+)$/gm, '• $1')
            .replace(/^\* (.+)$/gm, '• $1');

        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    };

    if (!versionData) return null;

    return (
        <Modal show={show} onHide={handleDismiss} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <HeaderIcon>🚀</HeaderIcon>
                    {t('version:title', "What's New")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Version header */}
                <div className="d-flex align-items-center mb-3">
                    <VersionBadge bg={versionData.is_major ? 'danger' : 'primary'}>
                        {versionData.is_major && '⭐ '}
                        v{versionData.version}
                    </VersionBadge>
                    <span className="text-muted">
                        {formatDate(versionData.release_date)}
                    </span>
                </div>

                {/* Version content */}
                <ContentContainer>
                    {renderContent(versionData.content)}
                </ContentContainer>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleDismiss}>
                    {t('version:remindLater', 'Remind me later')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleAcknowledge}
                    disabled={acknowledging}
                >
                    {acknowledging ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            {t('common:status.loading', 'Loading...')}
                        </>
                    ) : (
                        t('version:gotIt', 'Got it!')
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VersionUpdateModal;
